import { i18n } from '../../i18n/i18n'

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { connect, ConnectedProps } from 'react-redux'
import { find, includes } from 'lodash'
import routes from '../../routes/routes'
import { RootState } from '../../store'
import palletApi, {
  PalletStatus, PalletStatusData
} from '../../apis/pallet.api'
import inventoryApi, {
  InventoryReportExportRequest
} from '../../apis/inventory.api'
import confirmService from '../../services/confirm.service'
import downloadService from '../../services/download.service'
import printService from '../../services/print.service'
import alertService from '../../services/alert.service'
import itemPrintableService from '../../services/itemPrintable.service'
import { ConfirmParams } from '../../components/Confirm/Confirm.state'
import {
  replacePage,
  replaceLimit,
  replaceSearch,
  replaceStatus,
  replaceFilter,
  replaceSelectedItem,
  replaceSelectedWorkOrder,
  replaceLPN,
  replaceAllFilters
} from './Inventories.slice'
import { paginate, deleteInventory } from './Inventories.thunk'
import Card from '../../components/Card'
import ContentLayout from '../../components/ContentLayout'
import ContentMain from '../../components/ContentMain'
import DataTable from '../../components/DataTable'
import { TableHorizontalHeader } from '../../components/TableHorizontal'
import { ActionName } from '../../components/Actions'
import HeadingRow from '../../components/HeadingRow'
import RangeExport from '../../components/RangeExport'
import Select from '../../components/Select'
import SearchPicker, { SearchPickerOption } from '../../components/SearchPicker'
import itemService from '../../services/item.service'
import itemApi, { ItemData } from '../../apis/item.api'
import workOrderService from '../../services/workOrder.service'
import workOrderApi, { WorkOrderData } from '../../apis/workOrder.api'
import InputScan from '../../components/InputScan'
import Row from '../../components/Row/Row'
import Button from '../../components/Button'
import { WatermarkHelper } from '../../components/WatermarkHelper'

interface Props extends RouteComponentProps, ConnectedProps<typeof connector> {
}

interface State {
    item: string
    items: ItemData[]
    itemId: number | null
    workOrder: string
    workOrderId: number | null
    workOrders: WorkOrderData[]
    code: string
}

class Inventories extends Component<Props, State> {
  private rangeExportRef = React.createRef<{ reset:() => void }>()

  palletStatuses: PalletStatusData[] = [
    { name: 'Inventory', value: 'inventory' },
    { name: 'Consumed', value: 'consumed' },
    { name: 'Shipped', value: 'shipped' }
  ]

  headers: TableHorizontalHeader[] = [
    { heading: i18n('Item'), path: 'item.code' },
    { heading: i18n('Work order'), path: 'workOrderCode' },
    { heading: i18n('Code'), path: 'code' },
    { heading: i18n('Pallet'), path: 'number' },
    {
      heading: i18n('Quantity'),
      path: 'quantity',
      tooltip: ['item.category.name']
    },
    { heading: i18n('Total QPC'), path: 'unitQuantity' },
    { heading: i18n('Location'), path: 'locationName' },
    {
      heading: i18n('Created at'),
      path: 'lastInternalTransfer.inventory.createdAt',
      date: true,
      tooltip: ['inventory.createdUser.username']
    },
    { heading: i18n('Actions') }
  ]

  constructor (props: Props) {
    super(props)
    this.handleExportClick = this.handleExportClick.bind(this)
    this.handleFilterClick = this.handleFilterClick.bind(this)
    this.handleActionClick = this.handleActionClick.bind(this)
    this.handleLimitChange = this.handleLimitChange.bind(this)
    this.handleSearch = this.handleSearch.bind(this)
    this.handleStatusChange = this.handleStatusChange.bind(this)
    this.handlePaginate = this.handlePaginate.bind(this)

    this.handleItemSearch = this.handleItemSearch.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleItemSelect = this.handleItemSelect.bind(this)
    this.handleItemBlur = this.handleItemBlur.bind(this)

    this.handleWorkOrderSearch = this.handleWorkOrderSearch.bind(this)
    this.handleWorkOrderSelect = this.handleWorkOrderSelect.bind(this)
    this.handleWorkOrderBlur = this.handleWorkOrderBlur.bind(this)

    this.handleRefresh = this.handleRefresh.bind(this)

    this.state = {
      item: '',
      items: [],
      itemId: null,
      workOrder: '',
      workOrderId: null,
      workOrders: [],
      code: ''
    }
  }

  get itemSearchOptions () {
    return itemService.searchOptions(this.state.items)
  }

  get workOrderOptions () {
    return workOrderService.searchOptions(this.state.workOrders)
  }

  searchItem (term: string) {
    itemApi.search(term, items => {
      if (!items) return
      this.setState({ items })
    })
  }

  handleWorkOrderBlur (name: string) {
    this.setState({ workOrders: [] })
  }

  handleWorkOrderSelect (name: string, option: SearchPickerOption) {
    const workOrderId = option.id as number
    this.setState({ workOrderId, workOrders: [] })
    this.props.replaceSelectedWorkOrder(workOrderId.toString())
  }

  handleWorkOrderSearch (name: string, code: string) {
    workOrderApi.searchByCode(code, workOrders => {
      if (!workOrders) return
      this.setState({ workOrders })
    })
  }

  handleInputChange (name: string, value: string) {
    if (name === 'item') this.setState({ itemId: null, item: value })
    if (name === 'workOrder') this.setState({ workOrderId: null, workOrder: value })
    if (name === 'code') {
      this.setState({ code: value })
      if (value.length > 4 || value.length === 0) {
        this.props.replaceLPN(value)
      }
    }
  }

  handleItemSearch (name: string, term: string) {
    this.searchItem(term)
  }

  handleItemSelect (name: string, option: SearchPickerOption) {
    const itemId = option.id as number
    this.setState({ itemId, items: [] })
    this.props.replaceSelectedItem(itemId.toString())
  }

  handleItemBlur () {
    this.setState({ items: [] })
  }

  handleLimitChange (limit: string) {
    this.props.replaceLimit(Number(limit))
  }

  handleSearch (search: string) {
    if (search.length < 5 && search.length > 1) {
      console.log('Not 5 chars, returning')
      return
    }

    this.props.replaceSearch(search)
  }

  handlePaginate (page: number) {
    this.props.replacePage(page)
  }

  handleStatusChange (name: string, value: string) {
    const status = value as PalletStatus
    this.props.replaceStatus(status)
  }

  handleExportClick (startAt: string, endAt: string) {
    const { status } = this.props
    const payload: InventoryReportExportRequest = {}
    if (startAt) payload.startAt = startAt
    if (endAt) payload.endAt = endAt
    if (status) payload.status = status
    inventoryApi.exportReport(payload, report => {
      if (!report) {
        alertService.info('No results to export')
        return
      }
      downloadService.inventories(report)
    })
  }

  handleFilterClick (startAt: string, endAt: string) {
    this.props.replaceFilter(startAt, endAt)
  }

  handleActionClick (id: number, action: ActionName) {
    const { history } = this.props
    if (action === 'view') {
      const pathname = routes.inventory
      const state = { id }
      history.push({ pathname, state })
    } else if (action === 'history') {
      const pathname = routes.inventoryHistory
      const state = { id }
      history.push({ pathname, state })
    } else if (action === 'print') {
      const confirm: ConfirmParams = {
        id,
        question: i18n('Are you sure to print the label again?'),
        confirmText: i18n('Yes'),
        cancelText: i18n('No'),
        onConfirm: () => this.handlePrintConfirm(id)
      }
      confirmService.push(confirm)
    } else if (action === 'rollback') {
      const confirm: ConfirmParams = {
        id,
        question: i18n('Are you sure to rollback the last transaction?'),
        confirmText: i18n('Yes'),
        cancelText: i18n('No'),
        onConfirm: () => this.handleRollbackConfirm(id)
      }
      confirmService.push(confirm)
    } else if (action === 'delete') {
      const confirm: ConfirmParams = {
        id,
        type: 'warning',
        question: i18n('This inventory will be entirely removed from the system!'),
        confirmText: i18n('Delete'),
        cancelText: i18n('Cancel'),
        onConfirm: () => this.props.deleteInventory(id)
      }
      confirmService.push(confirm)
    }
  }

  handlePrintConfirm (palletId: number) {
    const pallet = find(this.props.data, { id: palletId })
    if (!pallet || !pallet.inventory || !pallet.item) return

    const labels = itemPrintableService.printableNames(pallet.item.printables)
    const printCases = includes(labels, 'cases')
    const printPallet = includes(labels, 'pallet')
    const inventoryId = pallet.inventory.id

    if (printCases && printPallet) {
      inventoryApi.printLabels(inventoryId, markups => {
        if (!markups) return
        WatermarkHelper.printWithWM(markups)
      })
    } else if (printCases) {
      inventoryApi.printCases(inventoryId, markups => {
        if (!markups) return
        WatermarkHelper.printWithWM(markups)
      })
    } else if (printPallet) {
      palletApi.print(palletId, markups => {
        if (!markups) return
        WatermarkHelper.printWithWM(markups)
      })
    }
  }

  handleRollbackConfirm (id: number) {
    palletApi.rollbackTransfer(id, transfer => {
      if (!transfer) return
      alertService.success('Last transfer rollback is done')
      this.props.paginate()
    })
  }

  handleRefresh () {
    // Reset all local state
    this.setState({
      item: '',
      items: [],
      itemId: null,
      workOrder: '',
      workOrderId: null,
      workOrders: [],
      code: ''
    })

    // Reset the date picker component's internal state
    if (this.rangeExportRef.current) {
      this.rangeExportRef.current.reset()
    }

    // Reset all Redux state at once
    this.props.replaceAllFilters()
    this.props.replaceFilter('', '')
    this.props.replaceSearch('')
    this.props.replaceStatus('inventory')
    this.props.replacePage(1)
    this.props.replaceLimit(10)
    this.props.replaceSelectedItem('')
    this.props.replaceSelectedWorkOrder('')
    this.props.replaceLPN('')

    // Wait for next tick before paginating
    setTimeout(() => {
      this.props.paginate()
    }, 0)
  }

  componentDidMount () {
    this.props.paginate()
  }

  render () {
    return (
            <ContentLayout>
                <ContentMain layout="full">
                    <Card>
                        <HeadingRow
                            heading={i18n('Inventories')}
                            doc='inventories'>
                            <RangeExport
                                ref={this.rangeExportRef}
                                onExport={this.handleExportClick}
                                onFilter={this.handleFilterClick}/>
                            <Button
                                text={i18n('Refresh')}
                                onClick={this.handleRefresh}
                                height={37}/>
                        </HeadingRow>

                        <Row column={3}>
                            <SearchPicker
                                name="item"
                                value={this.state.item}
                                options={this.itemSearchOptions}
                                label={i18n('Item')}
                                onSearch={this.handleItemSearch}
                                onChange={this.handleInputChange}
                                onSelect={this.handleItemSelect}
                                onBlur={this.handleItemBlur}/>
                            <SearchPicker
                                name="workOrder"
                                value={this.state.workOrder}
                                options={this.workOrderOptions}
                                label={i18n('Work order')}
                                onSearch={this.handleWorkOrderSearch}
                                onChange={this.handleInputChange}
                                onSelect={this.handleWorkOrderSelect}
                                onBlur={this.handleWorkOrderBlur}/>
                            <InputScan
                                name="code"
                                value={this.state.code}
                                label={i18n('Scan pallet code')}
                                autofocus={true}
                                onChange={this.handleInputChange}
                                onScan={this.handleInputChange}/>
                        </Row>
                        <div className="grid lg:grid-cols-5">
                            <Select
                                name="status"
                                value={this.props.status}
                                options={this.palletStatuses}
                                onChange={this.handleStatusChange}/>
                        </div>
                        <DataTable
                            headers={this.headers}
                            data={this.props.data}
                            total={this.props.total}
                            from={this.props.from}
                            to={this.props.to}
                            currentPage={this.props.currentPage}
                            lastPage={this.props.lastPage}
                            limit={String(this.props.limit)}
                            search={this.props.search}
                            onLimtChange={this.handleLimitChange}
                            onSearch={this.handleSearch}
                            onPaginate={this.handlePaginate}
                            onActionClick={this.handleActionClick}/>
                    </Card>
                </ContentMain>
            </ContentLayout>
    )
  }
}

const mapStateToProps = (state: RootState) => {
  const { result, limit, search, status } = state.inventories
  const { data, total, from, to, currentPage, lastPage } = result
  return { data, total, from, to, currentPage, lastPage, limit, search, status }
}

const mapDispatchToProps = {
  paginate,
  deleteInventory,
  replacePage,
  replaceLimit,
  replaceSearch,
  replaceStatus,
  replaceFilter,
  replaceSelectedItem,
  replaceSelectedWorkOrder,
  replaceLPN,
  replaceAllFilters
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(withRouter(Inventories))
